import React, { useState, useEffect } from 'react';
import { getProducts } from '../lib/db';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';

interface BulkPushModalProps {
  onSubmit: (data: {
    clientNumbers: string[];
    category: string;
    note: string;
    price: string;
  }) => void;
  onClose: () => void;
}

export function BulkPushModal({ onSubmit, onClose }: BulkPushModalProps) {
  const [clientInput, setClientInput] = useState('');
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    note: '',
    price: ''
  });
  const [description, setDescription] = useState<string[]>([]);

  useEffect(() => {
    loadClientNumbers();
  }, []);

  const loadClientNumbers = async () => {
    try {
      const products = await getProducts('draft');
      const uniqueClients = [...new Set(products.map(p => p.client_number))];
      setSuggestions(uniqueClients);
    } catch (error) {
      console.error('Erreur lors du chargement des numéros clients:', error);
    }
  };

  const handleClientInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setClientInput(value);

      if (value.length > 0) {
        const matchingSuggestions = suggestions.filter(s =>
          s.startsWith(value) && !selectedClients.includes(s)
        );
        setShowSuggestions(matchingSuggestions.length > 0);
      } else {
        setShowSuggestions(false);
      }
    }
  };

  const addClient = (clientNumber: string) => {
    if (!selectedClients.includes(clientNumber) && clientNumber.trim()) {
      setSelectedClients([...selectedClients, clientNumber]);
      setClientInput('');
      setShowSuggestions(false);
    }
  };

  const removeClient = (clientNumber: string) => {
    setSelectedClients(selectedClients.filter(c => c !== clientNumber));
  };

  const handleClientKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && clientInput.trim()) {
      e.preventDefault();
      addClient(clientInput);
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category: categoryId
    }));
    setDescription(prev => [...prev, categoryId]);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const lines = e.target.value.split('\n').filter(line => line.trim());
    setDescription(lines);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setFormData(prev => ({ ...prev, price: value }));
    }
  };

  const categories = [
    { id: 'Haut', label: 'Haut', color: 'bg-gray-200' },
    { id: 'Pant', label: 'Pant', color: 'bg-cyan-200' },
    { id: 'Robe', label: 'Robe', color: 'bg-pink-200' },
    { id: 'Jeans', label: 'Jeans', color: 'bg-green-100' },
    { id: 'Jupe', label: 'Jupe', color: 'bg-yellow-200' },
    { id: 'Acces', label: 'Acces', color: 'bg-blue-100' },
    { id: 'Promo', label: 'Promo', color: 'bg-red-200' },
    { id: 'Alim', label: 'Alim', color: 'bg-yellow-100' },
    { id: 'Autre', label: 'Autre', color: 'bg-purple-200' },
    { id: 'ENS', label: 'ENS', color: 'bg-indigo-200' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedClients.length === 0) {
      toast.error('Veuillez sélectionner au moins un client');
      return;
    }

    if (description.length === 0) {
      toast.error('Veuillez ajouter au least une description');
      return;
    }

    const price = parseFloat(formData.price);
    if (!formData.price || isNaN(price) || price <= 0) {
      toast.error('Veuillez entrer un prix valide');
      return;
    }

    try {
      await onSubmit({
        clientNumbers: selectedClients,
        category: formData.category,
        note: description.join('\n'),
        price: price.toString()
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout du produit en masse:', error);
      toast.error('Erreur lors de l\'ajout du produit');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[90vh]">
      <div className="overflow-y-auto flex-1 pr-2 space-y-4">
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numéros de Clients:
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={clientInput}
                onChange={handleClientInputChange}
                onKeyPress={handleClientKeyPress}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSuggestions(false);
                  }, 200);
                }}
                placeholder="Tapez un numéro..."
                className="w-full p-2 border rounded-md text-sm"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full bg-white border rounded-md mt-1 shadow-lg max-h-48 overflow-y-auto">
                  {suggestions
                    .filter(s => s.startsWith(clientInput) && !selectedClients.includes(s))
                    .map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => addClient(suggestion)}
                      >
                        {suggestion}
                      </div>
                    ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => addClient(clientInput)}
              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium whitespace-nowrap"
            >
              Ajouter
            </button>
          </div>
        </div>

        {selectedClients.length > 0 && (
          <div className="p-2 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-xs font-medium text-blue-800 mb-2">
              {selectedClients.length} client{selectedClients.length > 1 ? 's' : ''} sélectionné{selectedClients.length > 1 ? 's' : ''}
            </p>
            <div className="flex flex-wrap gap-1 max-h-[120px] overflow-y-auto">
              {selectedClients.map((client) => (
                <div
                  key={client}
                  className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs whitespace-nowrap"
                >
                  <span>{client}</span>
                  <button
                    type="button"
                    onClick={() => removeClient(client)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Catégorie:
          </label>
          <div className="flex flex-wrap gap-1">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => handleCategoryClick(cat.id)}
                className={`px-2 py-1 rounded text-xs ${cat.color} hover:opacity-80 transition-opacity`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description:
          </label>
          <textarea
            value={description.join('\n')}
            onChange={handleDescriptionChange}
            className="w-full p-2 border rounded-md min-h-[80px] bg-white text-sm"
            placeholder="Cliquez sur les catégories ou modifiez directement le texte ici"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Prix:
          </label>
          <input
            type="number"
            value={formData.price}
            onChange={handlePriceChange}
            className="w-full p-2 border rounded-md text-sm"
            step="0.01"
            min="0.01"
            required
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="border-t border-gray-200 mt-4 pt-4 bg-white sticky bottom-0 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-3 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-sm font-medium"
        >
          Fermer
        </button>
        <button
          type="submit"
          className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm font-medium"
        >
          Ajouter aux {selectedClients.length} panier{selectedClients.length > 1 ? 's' : ''}
        </button>
      </div>
    </form>
  );
}
